import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AppService } from '../../../services';
import { NgxSkeletonLoaderConfig } from 'ngx-skeleton-loader';
import { MapService } from 'src/app/shared/services/map.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../state/app.state';
import { CommonService } from '../../../services/common.service';
import { GetVideoIdRequest, UploadFileRequest } from '../../../core/shared.actions';
import { ProjectsService } from '../../../../modules/projects/core/projects.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MarkupService } from '../../../services/markup.service';
import { classifyFile } from 'src/app/shared/helpers';

@Component({
    selector: 'app-pictures-section-with-tags',
    templateUrl: './pictures-section-with-tags.component.html',
    styleUrls: ['./pictures-section-with-tags.component.scss'],
    standalone: false
})
export class PicturesSectionWithTagsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInputRef: any;
  @Input() pictures: any[] = [];
  @Input() isEditable = true;
  @Input() add = false;
  @Input() maxHeight?: string;
  @Input() label = '';
  @Input() tags = '';
  /** Passed to the file picker's `accept` attribute; empty = any file. */
  @Input() accept = '';
  /** When false, videos are refused with a message (the video upload flow is not wired for every caller). */
  @Input() allowVideo = true;
  @Output() onDelete: EventEmitter<any> = new EventEmitter();
  loading = true;
  @Input() selectedLineItem;

  loaderAppearance: NgxSkeletonLoaderConfig['appearance'] = '';
  loaderTheme: NgxSkeletonLoaderConfig['theme'] = {
    width: '100%',
    height: '100%',
  };
  dummyUrl = 'assets/images/png/video-processing.png';
  limits: any;
  markupPreviews: { [id: string]: string } = {};
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public appService: AppService,
    public mapService: MapService,
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private cdRef: ChangeDetectorRef,
    private markupService: MarkupService,
  ) {
  }

  ngOnInit() {
    this.markupService.markupSaved$.pipe(takeUntil(this.onDestroy)).subscribe(({ attachmentId, previewDataUrl }) => {
      if (attachmentId && previewDataUrl) {
        this.markupPreviews[attachmentId] = previewDataUrl;
        // Markup is saved via its own API — don't mark parent form dirty
      }
      this.cdRef.detectChanges();
    });
  }

  getThumbUrl(picture: any): string {
    // `thumb_url` is generated lazily by the image-processing pipeline; for
    // freshly-uploaded or sample-record attachments it can be null while
    // `original_url` already points at the S3 source. Fall through to the
    // original so the thumbnail renders (vs. an `src=""` 404) until the
    // background job populates thumb_url.
    return this.markupPreviews[picture?.id]
      || this.markupService.getMarkupPreview(picture?.id)
      || picture?.thumb_url
      || picture?.preview_url
      || picture?.original_url;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedLineItem']) {
      this.selectedLineItem = changes['selectedLineItem'];
    }

    this.pictures = [];

    this.projectsService.mediaData
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const list = data || [];
        const tempTag = this.tags?.replace(/_/g, '');
        this.pictures = list.filter(o =>
          this.tags
            ? o.tags?.[0] === tempTag
            : !o.tags?.length,
        );

        this.cdRef.detectChanges();
      });
  }

  hideLoader() {
    this.loading = false;
  }

  handleError() {
    this.hideLoader();
  }

  deletePicture(picture) {
    this.onDelete.emit(picture);
  }

  openSideNavPictures(picture) {
    if (this.selectedLineItem) {

      if (!this.selectedLineItem || !picture || !picture.id) {
        console.error('Invalid selectedLineItem or picture data');
        return;
      }

      const imageData = structuredClone(this.selectedLineItem.currentValue);

      if (!imageData || !Array.isArray(imageData.pictures) || imageData.pictures.length === 0) {
        console.error('Invalid or empty pictures array in imageData');
        return;
      }

      imageData.markerCategory = 'images';

      // Efficiently regroup the pictures array based on picture.id
      const selectedPictures = imageData.pictures.filter((img: any) => img.id === picture.id);
      const remainingPictures = imageData.pictures.filter((img: any) => img.id !== picture.id);

      // Ensure selected picture exists before modifying the array
      if (selectedPictures.length > 0) {
        imageData.pictures = selectedPictures.concat(remainingPictures);
      }
      if (!imageData?.comment) {
        imageData.comment = imageData.title;

      }

      this.mapService.selectedPicturesData.next(imageData);
      this.mapService.openSidenavOnMarkerImageClick(imageData);

    }

  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files; // Get selected files
    const imageFormData = new FormData();
    const videoFiles: File[] = [];
    let refusedVideo = false;

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);

      if (classifyFile(file) === 'video') {
        if (!this.allowVideo) {
          refusedVideo = true;
          continue;
        }
        videoFiles.push(file);
      } else {
        imageFormData.append('inputFiles', file);
        imageFormData.append(`fileTags[${i}][tags]`, this.tags);
      }
    }

    if (refusedVideo) {
      this.commonService.notification('Videos can not be attached here. Attach images or documents instead.', 'error');
      // Clear the picker so choosing the same file again still fires (change).
      event.target.value = '';
    }

    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));
    }

    if (videoFiles.length > 0) {
      videoFiles.forEach((videoFile: File) => {
        const videoElement = document.createElement('video');
        const reader = new FileReader();

        reader.onloadend = () => {
          videoElement.src = reader.result as string;

          videoElement.onloadedmetadata = () => {
            const videoDuration = videoElement.duration;
            const maxDuration = this.limits.video_size_in_secs;

            if (videoDuration > maxDuration) {
              this.commonService.notification(
                `Video duration is ${videoDuration} seconds, should be less than ${maxDuration} seconds.`,
                'success',
              );
            } else {
              this.store.dispatch(GetVideoIdRequest({ payload: videoFile }));
            }
          };
        };

        reader.readAsDataURL(videoFile);
      });
    }

    this.fileInputRef.nativeElement.value = '';
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
